// inventoryCogs.js — 월별 매출원가(COGS)·재고 증감 계산
//
// 회계 항등식:  매출원가 = 기초재고 + 당기매입 − 기말재고
//
// 이 앱이 가진 세 가지 자료를 그대로 연결한다.
//   · 매출액   : 이카운트 매출(salesDaily)      — supplyAmount(공급가액, 부가세 제외)
//   · 매입액   : 매입세금계산서(purchaseInvoice) — 공급가액(부가세 제외)
//   · 재고금액 : 일일재고기록의 재고금액(valueHistory) — 입고단가(부가세 제외) × 재고량
// 세 지표 모두 부가세 제외 기준이라 그대로 더하고 빼도 기준이 어긋나지 않는다.
//
// ⚠️ 매출 레코드의 amount는 2026-08-12부터 "부가세 포함" 금액이다(실제 입금 기준).
// 여기서 그대로 쓰면 매출만 10% 부풀어 매출총이익률이 실제보다 높게 나오므로,
// 원가·마진 계산에는 반드시 supplyAmount(공급가액)를 쓴다.
//
// 주의: 재고금액은 "그날 스냅샷을 찍었을 때"만 존재한다. 그래서 각 월의 기말재고는
// 그 달에 기록된 마지막 스냅샷을 쓰고, 기초재고는 직전 달의 기말재고를 쓴다.
// 둘 중 하나라도 없는 달은 매출원가를 계산하지 않고 null로 남긴다(추정하지 않는다).

const monthOf = (d) => String(d || "").slice(0, 7);

// valueHistory({ "2026-06-30": 552510623, ... }) → { "2026-06": {date, value} }
// 한 달에 여러 번 기록됐으면 그 달의 마지막 날짜를 기말재고로 본다.
export function monthEndInventory(valueHistory) {
  const out = {};
  Object.keys(valueHistory || {}).forEach((date) => {
    const v = valueHistory[date];
    if (typeof v !== "number" || !Number.isFinite(v)) return;
    const m = monthOf(date);
    if (!out[m] || date > out[m].date) out[m] = { date, value: v };
  });
  return out;
}

// 이전 달 문자열 ("2026-07" → "2026-06")
export function prevMonth(m) {
  const [y, mm] = String(m).split("-").map(Number);
  if (!y || !mm) return "";
  const d = new Date(y, mm - 2, 1); // mm-2 = 전월(0-based)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// 월별 합계 맵 만들기 (records: [{date, amount, supplyAmount}])
// supplyPreferred=true면 공급가액(supplyAmount)을 우선 사용한다 — 매출은 amount가 부가세 포함이라
// 매입·재고금액(둘 다 부가세 제외)과 기준을 맞추려면 공급가액으로 집계해야 한다.
function monthlySum(records, supplyPreferred = false) {
  const m = {};
  (records || []).forEach((r) => {
    const k = monthOf(r.date);
    const supply = Number(r.supplyAmount);
    const a = supplyPreferred && Number.isFinite(supply) ? supply : Number(r.amount);
    if (!k || !Number.isFinite(a)) return;
    m[k] = (m[k] || 0) + a;
  });
  return m;
}

/**
 * 월별 매출원가·재고증감 표를 만든다.
 * @param {Array}  salesRecords    매출 [{date, amount}]
 * @param {Array}  purchaseRecords 매입세금계산서 [{date, amount}]
 * @param {Object} valueHistory    { "YYYY-MM-DD": 재고금액(원) }
 * @returns {{rows:Array, totals:Object}}
 *   rows: [{ month, sales, purchase, beginInv, endInv, invChange, cogs,
 *            grossProfit, grossMargin, turnoverMonths, hasInv }]
 */
export function buildMonthlyCogs({ salesRecords, purchaseRecords, valueHistory }) {
  const salesM = monthlySum(salesRecords, true); // 매출은 공급가액 기준으로 집계(부가세 제외)
  const purchM = monthlySum(purchaseRecords);
  const invM = monthEndInventory(valueHistory);

  const months = Array.from(new Set([...Object.keys(salesM), ...Object.keys(purchM), ...Object.keys(invM)]))
    .filter(Boolean)
    .sort();

  const rows = months.map((month) => {
    const sales = salesM[month] || 0;
    const purchase = purchM[month] || 0;
    const end = invM[month] ? invM[month].value : null;
    const begin = invM[prevMonth(month)] ? invM[prevMonth(month)].value : null;
    const hasInv = begin != null && end != null;

    const invChange = hasInv ? end - begin : null;
    const cogs = hasInv ? begin + purchase - end : null;
    const grossProfit = hasInv ? sales - cogs : null;
    const grossMargin = hasInv && sales > 0 ? (grossProfit / sales) * 100 : null;
    // 재고회전(개월): 기말재고를 그 달 원가로 나눈 값 — 지금 재고가 몇 달치인지
    const turnoverMonths = hasInv && cogs > 0 ? end / cogs : null;

    return {
      month, sales, purchase,
      beginInv: begin, endInv: end, invChange,
      cogs, grossProfit, grossMargin, turnoverMonths, hasInv,
      endInvDate: invM[month] ? invM[month].date : "",
    };
  });

  const calc = rows.filter((r) => r.hasInv);
  const sum = (f) => calc.reduce((s, r) => s + (r[f] || 0), 0);
  const totalSales = sum("sales");
  const totalCogs = sum("cogs");
  const totals = {
    months: calc.length,
    sales: totalSales,
    purchase: sum("purchase"),
    cogs: totalCogs,
    grossProfit: totalSales - totalCogs,
    grossMargin: totalSales > 0 ? ((totalSales - totalCogs) / totalSales) * 100 : null,
    firstInv: calc.length ? calc[0].beginInv : null,
    lastInv: calc.length ? calc[calc.length - 1].endInv : null,
    invChange: calc.length ? calc[calc.length - 1].endInv - calc[0].beginInv : null,
  };

  return { rows, totals };
}
